import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { forgotPasswordApi, resetPasswordApi } from "../api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { normalizeApiError } from "../lib/apiError";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const c = params.get("code");
    const e = params.get("email");
    if (c) {
      setCode(c);
      setStep(2);
      if (e) setEmail(e);
      setMsg(t("auth.forgot_code_from_link"));
    }
  }, [params, t]);

  const onRequestCode = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await forgotPasswordApi(email.trim());
      if (r.code) {
        setDevCode(r.code);
        setMsg(t("auth.forgot_code_sent_demo", { email }));
      } else {
        setMsg(t("auth.forgot_code_sent"));
      }
      setStep(2);
    } catch (e2) {
      setErr(normalizeApiError(e2, t("common.error")));
    } finally { setBusy(false); }
  };

  const onReset = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== password2) {
      setErr(t("auth.passwords_mismatch"));
      return;
    }
    setBusy(true);
    try {
      await resetPasswordApi(code.trim(), password);
      navigate("/login", { replace: true });
    } catch (e2) {
      setErr(normalizeApiError(e2, t("auth.forgot_reset_error")));
    } finally { setBusy(false); }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card" data-testid="forgot-card">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Link to="/" className="small text-muted text-decoration-none"
                data-testid="forgot-back-home" title={t("auth.back_to_home")}>
            <i className="bi bi-arrow-left me-1"></i>{t("auth.back_to_home")}
          </Link>
          <LanguageSwitcher />
        </div>
        <Link to="/" className="text-decoration-none text-dark" data-testid="forgot-brand-home"
              title={t("auth.back_to_home")}>
          <h3 className="fw-bold mb-1">{t("auth.reset_password")}</h3>
        </Link>
        <p className="text-muted small mb-4">
          {step === 1 ? t("auth.forgot_step1_hint") : t("auth.forgot_step2_hint")}
        </p>

        {err && <div className="alert alert-danger py-2">{err}</div>}
        {msg && <div className="alert alert-info py-2">{msg}</div>}
        {devCode && step === 2 && (
          <div className="alert alert-warning py-2 small" data-testid="dev-code">
            {t("auth.forgot_dev_code_label")} <strong>{devCode}</strong>
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={onRequestCode}>
            <input type="email" className="form-control mb-3" required name="email"
                   placeholder={t("common.email")} value={email}
                   onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
            <button className="btn btn-primary w-100" disabled={busy} data-testid="forgot-request">
              {busy ? t("auth.forgot_sending") : t("auth.send_code")}
            </button>
          </form>
        ) : (
          <form onSubmit={onReset}>
            <div className="mb-3">
              <label className="form-label small">{t("auth.code")}</label>
              <input className="form-control" required name="code" value={code}
                     onChange={(e) => setCode(e.target.value)} data-testid="forgot-code" />
            </div>
            <div className="mb-3">
              <label className="form-label small">{t("auth.new_password")}</label>
              <input type="password" className="form-control" required minLength={6}
                     name="password" value={password}
                     onChange={(e) => setPassword(e.target.value)} data-testid="forgot-newpwd" />
            </div>
            <div className="mb-3">
              <label className="form-label small">{t("auth.password_repeat")}</label>
              <input type="password" className="form-control" required minLength={6}
                     name="password2" value={password2}
                     onChange={(e) => setPassword2(e.target.value)} data-testid="forgot-newpwd2" />
            </div>
            <button className="btn btn-primary w-100" disabled={busy} data-testid="forgot-reset">
              {busy ? t("auth.forgot_saving") : t("auth.change_password")}
            </button>
          </form>
        )}

        <div className="text-center mt-4 small">
          <Link to="/login">{t("auth.forgot_back_login")}</Link>
        </div>
      </div>
    </div>
  );
}
